import { FCC, FormattedDate, Label } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
	SelectableTravelSegmentIndexResource,
	TravelDirection,
	TravelSegmentIndexCarResource,
	TravelSegmentIndexFlightResource,
	TravelSegmentIndexShuttleResource,
	TravelSegmentIndexTrainResource,
} from "../../../../shared/types/travelSegment";
import { TravelSegmentDateRange } from "../TravelSegmentDateRange";
import { travelSegmentIdentifier } from "../travelSegmentIdentifier";
import { BaseCard, Information, Location, TravelIcon } from "./SelectableTravelSegmentBaseCard";

interface Props<T extends SelectableTravelSegmentIndexResource> {
	travelSegment: T;
	isActive?: boolean;
	noContingentAvailable?: boolean;
	direction?: TravelDirection;
}

const TagContainer = styled.div`
	position: absolute;
	top: ${({ theme }) => theme.spacing(2)};
	right: ${({ theme }) => theme.spacing(2)};
	display: flex;
	gap: ${({ theme }) => theme.spacing(2)};
`;

const Container = styled(BaseCard)`
	display: grid;
	position: relative;
	grid-template-columns: 1fr 3fr 3fr;
	min-height: 132px;
	gap: ${({ theme }) => theme.spacing(2)};

	${({ theme }) => theme.breakpoints.tablet} {
		gap: ${({ theme }) => theme.spacing(5)};
	}
`;

export const SelectableTravelSegmentCard: FC<Props<SelectableTravelSegmentIndexResource>> = ({
	travelSegment,
	isActive,
	direction,
}) => {
	const noContingentAvailable = !!(
		travelSegment.contingent &&
		travelSegment.travelCount &&
		travelSegment.contingent <= travelSegment.travelCount
	);

	switch (travelSegment.type) {
		case "flight":
			return (
				<FlightTravelSegmentCard
					travelSegment={travelSegment}
					isActive={isActive}
					noContingentAvailable={noContingentAvailable}
				/>
			);
		case "train":
			return (
				<TrainTravelSegmentCard
					travelSegment={travelSegment}
					isActive={isActive}
					noContingentAvailable={noContingentAvailable}
				/>
			);
		case "car":
			return (
				<CarTravelSegmentCard
					travelSegment={travelSegment}
					isActive={isActive}
					noContingentAvailable={noContingentAvailable}
					direction={direction}
				/>
			);
		case "shuttle":
			return (
				<ShuttleTravelSegmentCard
					travelSegment={travelSegment}
					isActive={isActive}
					noContingentAvailable={noContingentAvailable}
					direction={direction}
				/>
			);
	}
};

export const ContingentTag: FC = () => {
	const { t } = useTranslation();

	return (
		<TagContainer>
			<Label label={t("No contingent available")} size="small" color="warning">
				{t("No contingent available")}
			</Label>
		</TagContainer>
	);
};

const FlightTravelSegmentCard: FCC<Props<TravelSegmentIndexFlightResource>> = ({
	travelSegment,
	isActive,
	noContingentAvailable,
}) => {
	return (
		<Container $isActive={isActive} $hasNoContingent={noContingentAvailable}>
			{noContingentAvailable && <ContingentTag />}
			<TravelIcon src="/icons/travel.svg" />
			<Information>
				<strong>
					{travelSegment.departureTime} - {travelSegment.arrivalTime}
				</strong>
				<Location title={travelSegment.departureLocation?.name}>
					{travelSegment.departureLocation?.name} ({travelSegment.departureLocation?.iataCode})
				</Location>
				<span>{travelSegmentIdentifier(travelSegment)}</span>
			</Information>
			<Information>
				<TravelSegmentDateRange
					arrivalDate={travelSegment.arrivalDate ?? ""}
					departureDate={travelSegment.departureDate ?? ""}
				/>
				<Location title={travelSegment.arrivalLocation?.name}>
					{travelSegment.arrivalLocation?.name} ({travelSegment.arrivalLocation?.iataCode})
				</Location>
				<span>{travelSegment.serviceClass}</span>
			</Information>
		</Container>
	);
};

const TrainTravelSegmentCard: FCC<Props<TravelSegmentIndexTrainResource>> = ({
	travelSegment,
	isActive,
	noContingentAvailable,
}) => {
	return (
		<Container $isActive={isActive} $hasNoContingent={noContingentAvailable}>
			{noContingentAvailable && <ContingentTag />}
			<TravelIcon src="/icons/train.svg" />
			<Information>
				<strong>
					{travelSegment.departureTime} - {travelSegment.arrivalTime}
				</strong>
				<Location title={travelSegment.departureLocation}>{travelSegment.departureLocation}</Location>
				<span>{travelSegmentIdentifier(travelSegment)}</span>
			</Information>
			<Information>
				<TravelSegmentDateRange arrivalDate={travelSegment.arrivalDate} departureDate={travelSegment.departureDate} />
				<Location title={travelSegment.arrivalLocation}>{travelSegment.arrivalLocation}</Location>
				<span>{travelSegment.serviceClass}</span>
			</Information>
		</Container>
	);
};

const CarTravelSegmentCard: FCC<Props<TravelSegmentIndexCarResource>> = ({
	travelSegment,
	isActive,
	noContingentAvailable,
	direction,
}) => {
	return (
		<Container $isActive={isActive} $hasNoContingent={noContingentAvailable}>
			{noContingentAvailable && <ContingentTag />}
			<TravelIcon src="/icons/car.svg" />
			<Information>
				<strong>{direction === "arrival" ? travelSegment.arrivalTime : travelSegment.departureTime}</strong>
				<Location title={travelSegment.departureLocation}>{travelSegment.departureLocation || "-"}</Location>
				<span>{travelSegmentIdentifier(travelSegment)}</span>
			</Information>
			<Information>
				<FormattedDate
					date={direction === "arrival" ? travelSegment.arrivalDate : travelSegment.departureDate}
					format={{ day: "2-digit", month: "2-digit" }}
				/>
				<Location title={travelSegment.arrivalLocation}>{travelSegment.arrivalLocation || "-"}</Location>
				<span />
			</Information>
		</Container>
	);
};

const ShuttleTravelSegmentCard: FCC<Props<TravelSegmentIndexShuttleResource>> = ({
	travelSegment,
	isActive,
	noContingentAvailable,
}) => {
	return (
		<Container $isActive={isActive} $hasNoContingent={noContingentAvailable}>
			{noContingentAvailable && <ContingentTag />}
			<TravelIcon src="/icons/shuttle.svg" />
			<Information>
				<strong>
					{travelSegment.departureTime} - {travelSegment.arrivalTime}
				</strong>
				<Location title={travelSegment.departureLocation}>{travelSegment.departureLocation}</Location>
				<span>{travelSegmentIdentifier(travelSegment)}</span>
			</Information>
			<Information>
				<TravelSegmentDateRange arrivalDate={travelSegment.arrivalDate} departureDate={travelSegment.departureDate} />
				<Location title={travelSegment.arrivalLocation}>{travelSegment.arrivalLocation}</Location>
				<span />
			</Information>
		</Container>
	);
};
